import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../api/CheckApi';
import { moneyToText } from '../../api/utils';

const CheckPrint = () => {
  // --- STATE ---
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null); 
  const [modal, setModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  const todayISO = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    location: "ANKARA",
    date: todayISO,
    amount: "",
    amountText: "",
    currency: "TL"
  });

  const activeCompany = companies.find(c => c.id == selectedCompanyId);
  const activeTemplate = templates.find(t => t.id == selectedTemplateId);

  const filteredCompanies = companies.filter(c => {
    const term = searchTerm.toLowerCase();
    const titleMatch = c.title ? c.title.toLowerCase().includes(term) : false;
    const taxMatch = c.tax_number ? c.tax_number.includes(term) : false;
    return titleMatch || taxMatch;
  });

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadData = async () => {
    try {
      const [comps, temps] = await Promise.all([api.getCompanies(), api.getTemplates()]);
      setCompanies(comps);
      setTemplates(temps);
      if (comps.length > 0) setSelectedCompanyId(comps[0].id);
      if (temps.length > 0) setSelectedTemplateId(temps[0].id);
    } catch (error) { console.error(error); }
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({ ...prev, amount: val, amountText: moneyToText(val, prev.currency) }));
  };

  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    setFormData(prev => ({
        ...prev,
        currency: newCurrency,
        amountText: moneyToText(prev.amount, newCurrency)
    }));
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectCompany = (id) => {
    setSelectedCompanyId(id);
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const closeModal = () => setModal({ ...modal, isOpen: false });
  const showMessage = (type, title, message) => setModal({ isOpen: true, type, title, message });

  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split('-');
    return `${day}.${month}.${year}`;
  };

  const formatAmount = (value) => {
    if (!value) return "";
    const number = parseFloat(value);
    if (isNaN(number)) return "";
    return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(number);
  };

  const handlePrint = async () => {
    if (!activeCompany || !activeTemplate) return showMessage('error', 'Eksik Seçim', 'Lütfen bir Firma ve Şablon seçiniz.');
    if (!formData.amount || parseFloat(formData.amount) <= 0) return showMessage('error', 'Hatalı Tutar', 'Lütfen geçerli bir tutar giriniz.');

    try {
      await api.saveCheckHistory({
        company_name: activeCompany.title,
        company_tax_no: activeCompany.tax_number,
        amount: parseFloat(formData.amount),
        amount_text: formData.amountText,
        check_date: formatDateForDisplay(formData.date),
        check_location: formData.location,
        currency: formData.currency
      });

      setTimeout(() => { window.print(); }, 100);
    } catch (error) {
      console.error("Kayıt Hatası:", error);
      showMessage('error', 'Kayıt Hatası', 'Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  // --- STİLLER (Kurumsal Modernizasyon) ---
  const colors = {
    primary: '#1e3a8a', // Derin Kurumsal Mavi
    secondary: '#f8fafc', // Arka Plan Grisi
    border: '#cbd5e1', // Yumuşak Çizgi Rengi
    textMain: '#0f172a',
    textMuted: '#64748b'
  };

  const cardStyle = {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    marginBottom: '24px',
    border: `1px solid ${colors.border}`
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: colors.textMuted,
    marginBottom: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.textMain,
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const selectStyle = {
    ...inputStyle,
    backgroundColor: 'white',
    cursor: 'pointer'
  };

  const btnPrimaryStyle = {
    padding: '10px 24px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '8px',
    height: '42px',
    fontWeight: '600',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(30, 58, 138, 0.2)',
    transition: 'background-color 0.2s'
  };

  // Dropdown Stilleri
  const dropdownContainerStyle = { position: 'relative', flex: 1.5, minWidth: '250px' };
  const dropdownHeaderStyle = {
    ...selectStyle,
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    height: '42px'
  };
  const dropdownListStyle = {
    position: 'absolute', top: '100%', left: 0, right: 0,
    border: `1px solid ${colors.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px',
    background: 'white', zIndex: 1000, maxHeight: '250px', overflowY: 'auto',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
  };
  const dropdownSearchInputStyle = {
    width: '100%', padding: '12px', border: 'none', borderBottom: `1px solid ${colors.border}`,
    outline: 'none', background: colors.secondary, fontSize: '14px'
  };
  const dropdownItemStyle = (isSelected) => ({
    padding: '12px 14px', cursor: 'pointer',
    backgroundColor: isSelected ? '#eff6ff' : 'white',
    borderBottom: `1px solid ${colors.secondary}`,
    transition: 'background-color 0.1s'
  });

  // Modal Stilleri
  const modalOverlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backdropFilter: 'blur(4px)'
  };
  const modalBoxStyle = {
    backgroundColor: 'white', padding: '32px', borderRadius: '16px',
    width: '400px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)', textAlign: 'center'
  };

  // --- BASKI ALANI STİLLERİ (Değiştirilmedi) ---
  const printAreaStyle = {
    position: 'relative', width: '800px', height: '350px',
    border: 'none', margin: '20px auto', backgroundColor: 'white',
    padding: 0, overflow: 'hidden'
  };

  const itemStyle = (x, y, color = 'black', bold = true) => ({
    position: 'absolute', left: `${x}px`, top: `${y}px`,
    fontSize: '14px', fontFamily: 'Courier New, monospace',
    fontWeight: bold ? 'bold' : 'normal', color: color,
    whiteSpace: 'nowrap', zIndex: 10
  });
  const amountitemStyle = (x, y, color = 'black', bold = true) => ({
    position: 'absolute', left: `${x}px`, top: `${y}px`,
    fontSize: '13px', fontFamily: 'Courier New, monospace',
    fontWeight: bold ? 'bold' : 'normal', color: color,
    whiteSpace: 'nowrap', zIndex: 10
  });

  return (
    <div style={{ padding: '30px', backgroundColor: colors.secondary, minHeight: '100vh', fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        
        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h2 style={{ color: colors.textMain, margin: 0, fontSize: '24px', fontWeight: '700' }}>
               Çek Kesme İşlemleri
            </h2>
        </div>

        {/* --- FORM KARTI --- */}
        <div className="no-print" style={cardStyle}>
          
          {/* 1. SATIR: Firma ve Şablon Seçimi */}
          <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'flex-end' }}>
             
             <div style={dropdownContainerStyle} ref={dropdownRef}>
                <label style={labelStyle}>İlgili Firma</label>
                <div style={dropdownHeaderStyle} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                    <span style={{ color: activeCompany ? colors.textMain : colors.textMuted }}>
                      {activeCompany ? activeCompany.title : "Firma Seçiniz..."}
                    </span>
                    <span style={{fontSize:'12px', color: colors.textMuted}}>▼</span>
                </div>

                {isDropdownOpen && (
                  <div style={dropdownListStyle}>
                      <input 
                        autoFocus type="text" placeholder="🔍 Firma Adı veya Vergi No Ara..." 
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        style={dropdownSearchInputStyle} onClick={(e) => e.stopPropagation()} 
                      />
                      {filteredCompanies.length === 0 ? (
                          <div style={{padding:'12px', color:colors.textMuted, textAlign:'center'}}>Sonuç bulunamadı</div>
                      ) : (
                          filteredCompanies.map(c => (
                              <div 
                                  key={c.id} 
                                  style={dropdownItemStyle(c.id === selectedCompanyId)}
                                  onClick={() => handleSelectCompany(c.id)}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = c.id === selectedCompanyId ? '#eff6ff' : 'white'}
                              >
                                  <div style={{fontWeight:'600', color: colors.textMain}}>{c.title}</div>
                                  <div style={{fontSize:'12px', color: colors.textMuted, marginTop: '4px'}}>VKN: {c.tax_number}</div>
                              </div>
                          ))
                      )}
                  </div>
                )}
             </div>

             <div style={{ flex: 1 }}>
                 <label style={labelStyle}>Baskı Şablonu</label>
                 <select style={selectStyle} value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}>
                    {templates.length === 0 && <option>Şablon Yok!</option>}
                    {templates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                 </select>
             </div>

             <button onClick={handlePrint} style={btnPrimaryStyle} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#152b69'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary}>
               <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M6 9V2h12v7"></path><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"></path><path d="M6 14h12v8H6z"></path></svg>
               Çeki Yazdır
             </button>
          </div>
          
          {/* 2. SATIR: Çek Detayları */}
          <div style={{ borderTop: `1px solid ${colors.secondary}`, paddingTop: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr 2fr 1.5fr 1fr', gap: '16px' }}>
             <div>
                <label style={labelStyle}>Düzenlenme Yeri</label>
                <input name="location" value={formData.location} onChange={handleInputChange} style={inputStyle} placeholder="Örn: Ankara" />
             </div>
             <div>
                <label style={labelStyle}>Düzenlenme Tarihi</label>
                <input type="date" name="date" value={formData.date} onChange={handleInputChange} style={inputStyle} />
             </div>
             <div>
                <label style={labelStyle}>Tutar</label>
                <input type="number" name="amount" value={formData.amount} onChange={handleAmountChange} style={inputStyle} placeholder="0.00" />
             </div>
             <div>
                <label style={labelStyle}>Döviz</label>
                <select name="currency" value={formData.currency} onChange={handleCurrencyChange} style={selectStyle}>
                    <option value="TL">TL</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                </select>
             </div>
          </div>
          
          {/* 3. SATIR: Yazıyla Tutar */}
          <div style={{marginTop:'24px', backgroundColor: colors.secondary, padding: '16px', borderRadius: '8px', border: `1px solid ${colors.border}` }}>
               <label style={{...labelStyle, marginBottom: '8px'}}>Yazıyla Tutar (Otomatik Hesaplanır)</label>
               <input readOnly value={formData.amountText} style={{...inputStyle, backgroundColor: 'transparent', border: 'none', padding: 0, fontSize: '16px', fontWeight: '600', color: colors.primary}} placeholder="Tutar girildiğinde burada görünecektir..." />
          </div>
        </div>

        {/* --- BASKI ÖNİZLEME ALANI --- */}
        {activeTemplate ? (
          <div className="print-container" style={printAreaStyle}>
              {activeCompany && (
                  <>
                      <div style={itemStyle(activeTemplate.company_title_x, activeTemplate.company_title_y)}>{activeCompany.title}</div>
                      <div style={itemStyle(activeTemplate.tax_number_x, activeTemplate.tax_number_y)}>{activeCompany.tax_number}</div>
                      <div style={itemStyle(activeTemplate.tax_office_x, activeTemplate.tax_office_y)}>{activeCompany.tax_office}</div>
                  </>
              )}

              <div style={itemStyle(activeTemplate.date_x, activeTemplate.date_y)}>{formatDateForDisplay(formData.date)}</div>
              <div style={itemStyle(activeTemplate.location_x, activeTemplate.location_y)}>{formData.location}</div>
              <div style={itemStyle(activeTemplate.amount_x, activeTemplate.amount_y)}>#{formatAmount(formData.amount)}#</div>
              <div style={amountitemStyle(activeTemplate.amount_text_x, activeTemplate.amount_text_y, 'black', false)}>#{formData.amountText}#</div>
          </div>
        ) : (
          <div className="no-print" style={{padding:'40px', border:`2px dashed ${colors.border}`, borderRadius: '12px', textAlign:'center', color: colors.textMuted, backgroundColor: 'white'}}>
              Baskı önizlemesini görmek için lütfen yukarıdan bir şablon seçiniz.
          </div>
        )}

        {/* --- MODAL --- */}
        {modal.isOpen && (
          <div className="no-print" style={modalOverlayStyle}>
              <div style={modalBoxStyle}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                      {modal.type === 'error' ? '❌' : '✅'}
                  </div>
                  <h3 style={{ marginTop: 0, color: colors.textMain, fontSize: '20px' }}>
                      {modal.title}
                  </h3>
                  <p style={{ color: colors.textMuted, margin: '16px 0 24px 0', lineHeight: '1.5' }}>{modal.message}</p>
                  <button onClick={closeModal} style={{ ...btnPrimaryStyle, width: '100%', backgroundColor: modal.type === 'error' ? '#ef4444' : colors.primary }}>
                      Anladım
                  </button>
              </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CheckPrint;